/**
 * Clinical phrases for ChartMind translation.
 * Structured model: id, sourceText, targetText, languagePair, category.
 * Used for phrase cache lookups and optional Quick phrases UI.
 * Can later be loaded from config/Firestore; for now uses built-in default.
 */

/** @typedef {{ id: string, sourceText: string, targetText: string, languagePair: string, category: string }} ClinicalPhrase */

// Default phrase data by language pair (source → target map).
// Used to build CLINICAL_PHRASES and getPhraseMap. Can be replaced by config/Firestore later.
const PHRASES_BY_PAIR = {
  'en-US→sw-KE': {
    "Hello": "Habari",
    "How are you?": "Habari yako?",
    "How are you feeling today?": "Unajisikiaje leo?",
    "What brings you in today?": "Nini kimekufanya kuja leo?",
    "What is your name?": "Jina lako ni nani?",
    "How old are you?": "Una umri gani?",
    "Please sit down": "Tafadhali keti",
    "Thank you": "Asante",
    "You're welcome": "Karibu",
    "Where does it hurt?": "Inaumia wapi?",
    "Does it hurt here?": "Inaumia hapa?",
    "Point to where it hurts": "Onyesha mahali inapouuma",
    "How long have you had this pain?": "Umekuwa na maumivu haya kwa muda gani?",
    "Is the pain constant or does it come and go?": "Maumivu ni ya kudumu au yanakuja na kwenda?",
    "On a scale of 1 to 10, how bad is the pain?": "Kwa kiwango cha 1 hadi 10, maumivu ni makubwa kiasi gani?",
    "When did this start?": "Hii ilianza lini?",
    "Is it getting better or worse?": "Inakuwa bora au mbaya zaidi?",
    "Do you have a fever?": "Una homa?",
    "Do you have a headache?": "Una maumivu ya kichwa?",
    "Do you have a cough?": "Una kikohozi?",
    "Are you having trouble breathing?": "Una shida kupumua?",
    "Do you feel dizzy?": "Unajisikia kizunguzungu?",
    "Do you feel nauseous?": "Unajisikia kichefuchefu?",
    "Have you vomited?": "Umetapika?",
    "Do you have diarrhea?": "Una kuharisha?",
    "Are you constipated?": "Una kufunga choo?",
    "Do you have any allergies?": "Una mzio wowote?",
    "Are you allergic to any medications?": "Una mzio wa dawa yoyote?",
    "What medications are you currently taking?": "Unatumia dawa gani sasa hivi?",
    "Have you had any surgeries?": "Umewahi kufanyiwa upasuaji?",
    "Do you have diabetes?": "Una kisukari?",
    "Do you have high blood pressure?": "Una shinikizo la damu?",
    "Do you have heart problems?": "Una matatizo ya moyo?",
    "Do you have asthma?": "Una pumu?",
    "Are you pregnant?": "Una mimba?",
    "When was your last menstrual period?": "Hedhi yako ya mwisho ilikuwa lini?",
    "Do you smoke?": "Unavuta sigara?",
    "Do you drink alcohol?": "Unakunywa pombe?",
    "Take a deep breath": "Vuta pumzi kwa nguvu",
    "Breathe in": "Vuta pumzi",
    "Breathe out": "Toa pumzi",
    "Hold your breath": "Shika pumzi",
    "Open your mouth": "Fungua kinywa",
    "Say aah": "Sema aah",
    "Lie down please": "Tafadhali lala",
    "Stand up please": "Tafadhali simama",
    "Turn to your side": "Geuka upande",
    "Lift your arm": "Inua mkono wako",
    "Relax": "Pumzika",
    "This might hurt a little": "Hii inaweza kuuma kidogo",
    "I need to examine you": "Ninahitaji kukuchunguza",
    "I will take your blood pressure": "Nitapima shinikizo lako la damu",
    "I will take your temperature": "Nitapima joto lako",
    "I will listen to your heart": "Nitasikiliza moyo wako",
    "I will listen to your lungs": "Nitasikiliza mapafu yako",
    "I think you have": "Nadhani una",
    "You have an infection": "Una maambukizi",
    "You need antibiotics": "Unahitaji antibiotiki",
    "Take this medicine": "Tumia dawa hii",
    "Take one pill in the morning": "Tumia kidonge kimoja asubuhi",
    "Take one pill at night": "Tumia kidonge kimoja usiku",
    "Take with food": "Tumia pamoja na chakula",
    "Drink plenty of water": "Kunywa maji mengi",
    "Get plenty of rest": "Pumzika sana",
    "Come back if it gets worse": "Rudi kama itakuwa mbaya zaidi",
    "Come back in one week": "Rudi baada ya wiki moja",
    "You need to go to the hospital": "Unahitaji kwenda hospitali",
    "We need to do some tests": "Tunahitaji kufanya vipimo",
    "We need to do a blood test": "Tunahitaji kufanya kipimo cha damu",
    "We need to do an X-ray": "Tunahitaji kufanya X-ray",
    "Yes": "Ndiyo",
    "No": "Hapana",
    "I understand": "Ninaelewa",
    "Do you understand?": "Unaelewa?",
    "Please repeat that": "Tafadhali rudia",
    "Slowly please": "Polepole tafadhali",
    "Any questions?": "Una maswali?",
    "Do you have any questions?": "Una maswali yoyote?",
    "Is there anything else?": "Kuna kitu kingine?",
  },
  'sw-KE→en-US': {
    "Habari": "Hello",
    "Nzuri": "Good / Fine",
    "Sijisikii vizuri": "I'm not feeling well",
    "Nina maumivu": "I have pain",
    "Inaumia sana": "It hurts a lot",
    "Inaumia kidogo": "It hurts a little",
    "Ndiyo": "Yes",
    "Hapana": "No",
    "Sijui": "I don't know",
    "Naelewa": "I understand",
    "Sielewi": "I don't understand",
    "Tafadhali": "Please",
    "Asante": "Thank you",
    "Nina homa": "I have a fever",
    "Nina kikohozi": "I have a cough",
    "Nina maumivu ya kichwa": "I have a headache",
    "Nina maumivu ya tumbo": "I have stomach pain",
    "Nimekuwa na hii kwa siku tatu": "I've had this for three days",
    "Nimekuwa na hii kwa wiki moja": "I've had this for one week",
    "Inakuja na kwenda": "It comes and goes",
    "Ni ya kudumu": "It's constant",
    "Hapa": "Here",
    "Pale": "There",
    "Jana": "Yesterday",
    "Leo asubuhi": "This morning",
    "Usiku": "At night",
    "Baada ya kula": "After eating",
    "Kabla ya kula": "Before eating",
    "Sina mzio": "I have no allergies",
    "Nina mzio wa penicillin": "I'm allergic to penicillin",
    "Natumia dawa": "I take medication",
    "Situmii dawa": "I don't take medication",
    "Nina kisukari": "I have diabetes",
    "Nina shinikizo la damu": "I have high blood pressure",
    "Nimewahi fanyiwa upasuaji": "I've had surgery before",
    "Nina mimba": "I'm pregnant",
    "Sina mimba": "I'm not pregnant",
    "Sina maswali": "I have no questions",
    "Nina swali": "I have a question",
    "Ndio, nina maswali": "Yes, I have questions",
  },
  'en-US→es-ES': {
    "Hello": "Hola",
    "How are you?": "¿Cómo está usted?",
    "How are you feeling today?": "¿Cómo se siente hoy?",
    "What brings you in today?": "¿Qué lo trae hoy?",
    "What is your name?": "¿Cómo se llama?",
    "How old are you?": "¿Cuántos años tiene?",
    "Please sit down": "Por favor, siéntese",
    "Thank you": "Gracias",
    "You're welcome": "De nada",
    "Where does it hurt?": "¿Dónde le duele?",
    "Does it hurt here?": "¿Le duele aquí?",
    "Point to where it hurts": "Señale dónde le duele",
    "How long have you had this pain?": "¿Cuánto tiempo ha tenido este dolor?",
    "Is the pain constant or does it come and go?": "¿El dolor es constante o va y viene?",
    "On a scale of 1 to 10, how bad is the pain?": "En una escala del 1 al 10, ¿qué tan fuerte es el dolor?",
    "When did this start?": "¿Cuándo empezó esto?",
    "Is it getting better or worse?": "¿Está mejorando o empeorando?",
    "Do you have a fever?": "¿Tiene fiebre?",
    "Do you have a headache?": "¿Tiene dolor de cabeza?",
    "Do you have a cough?": "¿Tiene tos?",
    "Are you having trouble breathing?": "¿Tiene dificultad para respirar?",
    "Do you feel dizzy?": "¿Se siente mareado?",
    "Do you feel nauseous?": "¿Tiene náuseas?",
    "Have you vomited?": "¿Ha vomitado?",
    "Do you have diarrhea?": "¿Tiene diarrea?",
    "Are you constipated?": "¿Está estreñido?",
    "Do you have any allergies?": "¿Tiene alguna alergia?",
    "Are you allergic to any medications?": "¿Es alérgico a algún medicamento?",
    "What medications are you currently taking?": "¿Qué medicamentos está tomando actualmente?",
    "Have you had any surgeries?": "¿Ha tenido alguna cirugía?",
    "Do you have diabetes?": "¿Tiene diabetes?",
    "Do you have high blood pressure?": "¿Tiene presión alta?",
    "Do you have heart problems?": "¿Tiene problemas del corazón?",
    "Do you have asthma?": "¿Tiene asma?",
    "Are you pregnant?": "¿Está embarazada?",
    "When was your last menstrual period?": "¿Cuándo fue su última menstruación?",
    "Do you smoke?": "¿Fuma?",
    "Do you drink alcohol?": "¿Toma alcohol?",
    "Take a deep breath": "Respire profundo",
    "Breathe in": "Inhale",
    "Breathe out": "Exhale",
    "Hold your breath": "Aguante la respiración",
    "Open your mouth": "Abra la boca",
    "Say aah": "Diga aah",
    "Lie down please": "Acuéstese por favor",
    "Stand up please": "Levántese por favor",
    "Turn to your side": "Voltéese de lado",
    "Lift your arm": "Levante el brazo",
    "Relax": "Relájese",
    "This might hurt a little": "Esto puede doler un poco",
    "I need to examine you": "Necesito examinarlo",
    "I will take your blood pressure": "Voy a tomarle la presión",
    "I will take your temperature": "Voy a tomarle la temperatura",
    "I will listen to your heart": "Voy a escuchar su corazón",
    "I will listen to your lungs": "Voy a escuchar sus pulmones",
    "I think you have": "Creo que tiene",
    "You have an infection": "Tiene una infección",
    "You need antibiotics": "Necesita antibióticos",
    "Take this medicine": "Tome este medicamento",
    "Take one pill in the morning": "Tome una pastilla en la mañana",
    "Take one pill at night": "Tome una pastilla en la noche",
    "Take with food": "Tome con comida",
    "Drink plenty of water": "Tome mucha agua",
    "Get plenty of rest": "Descanse mucho",
    "Come back if it gets worse": "Regrese si empeora",
    "Come back in one week": "Regrese en una semana",
    "You need to go to the hospital": "Necesita ir al hospital",
    "We need to do some tests": "Necesitamos hacer algunas pruebas",
    "We need to do a blood test": "Necesitamos hacer un análisis de sangre",
    "We need to do an X-ray": "Necesitamos hacer una radiografía",
    "Yes": "Sí",
    "No": "No",
    "I understand": "Entiendo",
    "Do you understand?": "¿Entiende?",
    "Please repeat that": "Por favor repita eso",
    "Slowly please": "Despacio por favor",
    "Any questions?": "¿Tiene preguntas?",
    "Do you have any questions?": "¿Tiene alguna pregunta?",
    "Is there anything else?": "¿Hay algo más?",
  },
  'es-ES→en-US': {
    "Hola": "Hello",
    "Bien": "Good / Fine",
    "No me siento bien": "I'm not feeling well",
    "Me duele": "It hurts",
    "Duele mucho": "It hurts a lot",
    "Duele un poco": "It hurts a little",
    "Sí": "Yes",
    "No": "No",
    "No sé": "I don't know",
    "Entiendo": "I understand",
    "No entiendo": "I don't understand",
    "Por favor": "Please",
    "Gracias": "Thank you",
    "Tengo fiebre": "I have a fever",
    "Tengo tos": "I have a cough",
    "Tengo dolor de cabeza": "I have a headache",
    "Me duele el estómago": "I have stomach pain",
    "Tengo esto hace tres días": "I've had this for three days",
    "Tengo esto hace una semana": "I've had this for one week",
    "Va y viene": "It comes and goes",
    "Es constante": "It's constant",
    "Aquí": "Here",
    "Allí": "There",
    "Ayer": "Yesterday",
    "Esta mañana": "This morning",
    "En la noche": "At night",
    "Después de comer": "After eating",
    "Antes de comer": "Before eating",
    "No tengo alergias": "I have no allergies",
    "Soy alérgico a la penicilina": "I'm allergic to penicillin",
    "Tomo medicamentos": "I take medication",
    "No tomo medicamentos": "I don't take medication",
    "Tengo diabetes": "I have diabetes",
    "Tengo presión alta": "I have high blood pressure",
    "He tenido cirugía": "I've had surgery before",
    "Estoy embarazada": "I'm pregnant",
    "No estoy embarazada": "I'm not pregnant",
    "No tengo preguntas": "I have no questions",
    "Tengo una pregunta": "I have a question",
    "Sí, tengo preguntas": "Yes, I have questions",
  },
  'sw-KE→es-ES': {
    "Habari": "Hola",
    "Nzuri": "Bien",
    "Ndiyo": "Sí",
    "Hapana": "No",
    "Asante": "Gracias",
    "Tafadhali": "Por favor",
  },
  'es-ES→sw-KE': {
    "Hola": "Habari",
    "Bien": "Nzuri",
    "Sí": "Ndiyo",
    "No": "Hapana",
    "Gracias": "Asante",
    "Por favor": "Tafadhali",
  },
};

/** Build structured list from PHRASES_BY_PAIR for UI and extensibility. */
function buildClinicalPhrases() {
  /** @type {ClinicalPhrase[]} */
  const out = [];
  let id = 0;
  for (const [languagePair, map] of Object.entries(PHRASES_BY_PAIR)) {
    for (const [sourceText, targetText] of Object.entries(map)) {
      out.push({
        id: `phrase-${++id}`,
        sourceText,
        targetText,
        languagePair,
        category: 'general',
      });
    }
  }
  return out;
}

const CLINICAL_PHRASES = buildClinicalPhrases();

/**
 * Get phrase map for translation cache lookup: { [sourceText]: targetText }.
 * @param {string} fromLang
 * @param {string} toLang
 * @returns {{ [key: string]: string }}
 */
export function getPhraseMap(fromLang, toLang) {
  const key = `${fromLang}→${toLang}`;
  const map = PHRASES_BY_PAIR[key];
  return map ? { ...map } : {};
}

/**
 * Get structured clinical phrases for a language pair (for Quick phrases UI).
 * @param {string} fromLang
 * @param {string} toLang
 * @param {string} [category] - Optional filter by category (e.g. 'greeting', 'pain', 'instructions')
 * @returns {ClinicalPhrase[]}
 */
export function getClinicalPhrases(fromLang, toLang, category) {
  const pair = `${fromLang}→${toLang}`;
  let list = CLINICAL_PHRASES.filter((p) => p.languagePair === pair);
  if (category) {
    list = list.filter((p) => p.category === category);
  }
  return list;
}

/**
 * Get distinct categories for a language pair (for Quick phrases filter).
 * @param {string} fromLang
 * @param {string} toLang
 * @returns {string[]}
 */
export function getClinicalPhraseCategories(fromLang, toLang) {
  const pair = `${fromLang}→${toLang}`;
  const set = new Set(
    CLINICAL_PHRASES.filter((p) => p.languagePair === pair).map((p) => p.category)
  );
  return [...set];
}

export default {
  getPhraseMap,
  getClinicalPhrases,
  getClinicalPhraseCategories,
};
